// Чистата логика на „Замяна с взаимозаменяеми продукти" при разнасяне: кои
// продукти имат недостиг с възможни заместители, колко е наличен всеки донор и
// колко количество на донора струва едно покрито количество на получателя.
//
// ВАЖНО: секцията се строи от недостига ПРЕДИ замените (`rawShortage`) — иначе
// при частично покритие редът изчезва в мига, в който отметнеш заместител, и
// изборът не може да бъде отменен (същият капан като `rawDifference` в
// инвентаризацията).
//
// Количеството на донора се смята на ЕДНО място — `donorQuantityFor`. Вече
// избраните заместители на ДРУГИ получатели изяждат наличността на донора
// (`donorTaken`), иначе един донор се предлага два пъти за едно и също.
import type {
  InterchangeGroup,
  ReportPostingPreview,
  ReportSubstitutionItem,
  StockBalance,
} from "../../../../api/inventory";
import { QTY_EPSILON } from "./reportPostingShared";

// Количеството на донора (в НЕГОВИТЕ единици) за покрито количество на
// получателя — през коефициентите на общата им група. null = няма такава група.
export const donorQuantityFor = (
  groups: InterchangeGroup[],
  recipientProductId: number,
  donorProductId: number,
  recipientQuantity: number,
): number | null => {
  for (const group of groups) {
    const recipient = group.members.find(
      (member) => member.productId === recipientProductId,
    );
    const donor = group.members.find((member) => member.productId === donorProductId);
    if (recipient && donor)
      return (
        (recipientQuantity * (recipient.coefficient || 1)) / (donor.coefficient || 1)
      );
  }
  return null;
};

// Един възможен заместител на реда с недостиг.
export type DonorChoice = {
  donorId: number;
  name: string;
  donorUnit: string | null;
  // налично от донора в склада (в НЕГОВИТЕ единици), след взетото за други
  availableDonor: number;
  // същото в единици на получателя
  availableRecipient: number;
  // колко може да покрие МАКСИМУМ сега (в единици на получателя): наличното
  // му, но не повече от липсата минус покритото от другите заместители
  capRecipient: number;
  // избраното количество (в единици на получателя); null = не е отметнат
  chosen: number | null;
};

// Един продукт с недостиг, който има заместители (или вече избран такъв).
export type SubstitutionIssue = {
  recipientId: number;
  recipientName: string;
  unit: string | null;
  warehouseId: number;
  available: number; // наличното на получателя в склада (клампнато до 0)
  missing: number; // недостигът ПРЕДИ замените
  covered: number; // покритото от избраните заместители
  donors: DonorChoice[];
};

type Params = {
  preview: ReportPostingPreview | undefined;
  // складовете на всеки продукт (от алокациите) и балансьорът
  warehouseIdsOf: (productId: number) => number[];
  balancerOf: (productId: number) => number | null;
  effectiveQtyOf: (productId: number, warehouseId: number) => number;
  availableOf: (productId: number, warehouseId: number) => number;
  coveredOf: Map<number, number>;
  substitutions: ReportSubstitutionItem[];
  interchangeGroups: InterchangeGroup[];
  balancesByWarehouse: Map<number, Map<number, StockBalance>>;
};

export const buildSubstitutionIssues = ({
  preview,
  warehouseIdsOf,
  balancerOf,
  effectiveQtyOf,
  availableOf,
  coveredOf,
  substitutions,
  interchangeGroups,
  balancesByWarehouse,
}: Params): SubstitutionIssue[] => {
  const issues: SubstitutionIssue[] = [];
  for (const product of preview?.products ?? []) {
    const recipientId = product.productId;
    const covered = coveredOf.get(recipientId) ?? 0;
    const balancer = balancerOf(recipientId);
    const applied = substitutions.filter((sub) => sub.recipientProductId === recipientId);

    // Складът на замяната: този на вече избраните, иначе този с най-голям
    // недостиг ПРЕДИ замените (балансьорът носи покритото обратно).
    let warehouseId: number | null = applied[0]?.warehouseId ?? null;
    let missing = 0;
    const rawShortageAt = (id: number) =>
      effectiveQtyOf(recipientId, id) +
      (id === balancer ? covered : 0) -
      availableOf(recipientId, id);
    if (warehouseId != null) {
      missing = rawShortageAt(warehouseId);
    } else {
      for (const id of warehouseIdsOf(recipientId)) {
        const shortage = rawShortageAt(id);
        if (shortage > missing) {
          missing = shortage;
          warehouseId = id;
        }
      }
    }
    if (warehouseId == null || missing <= QTY_EPSILON) continue;

    // Взетото от всеки донор за ДРУГИ получатели в този склад
    const takenByOthers = new Map<number, number>();
    for (const sub of substitutions) {
      if (sub.recipientProductId === recipientId || sub.warehouseId !== warehouseId) continue;
      const taken = donorQuantityFor(
        interchangeGroups,
        sub.recipientProductId,
        sub.donorProductId,
        sub.quantity,
      );
      if (taken != null)
        takenByOthers.set(sub.donorProductId, (takenByOthers.get(sub.donorProductId) ?? 0) + taken);
    }

    const balances = balancesByWarehouse.get(warehouseId);
    const candidates = new Map<
      number,
      Omit<DonorChoice, "capRecipient"> & { sortIndex: number | null; date: string | null }
    >();
    for (const group of interchangeGroups) {
      const recipientMember = group.members.find((member) => member.productId === recipientId);
      if (!recipientMember) continue;
      const recipientCoefficient = recipientMember.coefficient || 1;
      for (const donor of group.members) {
        if (donor.productId === recipientId || candidates.has(donor.productId)) continue;
        const balance = balances?.get(donor.productId);
        const availableDonor = Math.max(
          0,
          (balance?.quantity ?? 0) - (takenByOthers.get(donor.productId) ?? 0),
        );
        const chosen =
          applied.find((sub) => sub.donorProductId === donor.productId)?.quantity ?? null;
        candidates.set(donor.productId, {
          donorId: donor.productId,
          name: donor.productName,
          donorUnit: balance?.unit ?? donor.unit ?? null,
          availableDonor,
          availableRecipient: (availableDonor * (donor.coefficient || 1)) / recipientCoefficient,
          chosen,
          sortIndex: donor.sortIndex,
          date: balance?.lastDeliveryDate ?? null,
        });
      }
    }
    // Приоритет: сорт индексът на групата, после най-старата доставка
    const donors = [...candidates.values()].sort((a, b) => {
      const sortA = a.sortIndex ?? Infinity;
      const sortB = b.sortIndex ?? Infinity;
      if (sortA !== sortB) return sortA - sortB;
      return (a.date ?? "").localeCompare(b.date ?? "");
    });
    // Заместители без наличност също се показват (изключени, „налични 0") —
    // иначе списъкът казва „със заместител", а тук няма нищо.
    if (!donors.length) continue;

    issues.push({
      recipientId,
      recipientName: product.productName,
      unit: product.unit,
      warehouseId,
      available: availableOf(recipientId, warehouseId),
      missing,
      covered,
      donors: donors.map((donor) => {
        const othersCovered = covered - (donor.chosen ?? 0);
        return {
          donorId: donor.donorId,
          name: donor.name,
          donorUnit: donor.donorUnit,
          availableDonor: donor.availableDonor,
          availableRecipient: donor.availableRecipient,
          chosen: donor.chosen,
          capRecipient: Math.max(
            0,
            Math.min(donor.availableRecipient, missing - othersCovered),
          ),
        };
      }),
    });
  }
  return issues;
};
